import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Check,
  Star,
  Users,
  Clock,
  BookOpen,
  Calendar,
  Shield,
  Loader2,
  AlertCircle,
  ChevronRight,
  Sparkles,
  Play
} from 'lucide-react'
import { commerceApi, formatPrice, getBillingPeriodLabel, type PricingResponse } from '@/api/commerce'
import { subscriptionApi } from '@/api/subscription'
import { useAuthStore } from '@/store/authStore'

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const [selectedPricingId, setSelectedPricingId] = useState<number | null>(null)

  // 상품 정보 조회
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['commerce', 'product', productId],
    queryFn: () => commerceApi.getProduct(Number(productId)),
    enabled: !!productId,
  })

  // 선택된 가격 옵션
  const selectedPricing = product?.pricingOptions.find(
    (p) => p.id === selectedPricingId
  ) || product?.pricingOptions.find((p) => p.isDefault) || product?.pricingOptions[0]

  // 선택된 pricing ID 설정
  if (product && !selectedPricingId && selectedPricing) {
    setSelectedPricingId(selectedPricing.id)
  }

  // 구독 정보 조회 (인증된 경우에만)
  const { data: mySubscriptions = [] } = useQuery({
    queryKey: ['my-subscriptions'],
    queryFn: () => subscriptionApi.getMySubscriptions(),
    enabled: isAuthenticated,
  })

  // 현재 상품에 대한 활성 구독 여부 확인
  const activeSubs = mySubscriptions.filter(s => s.status === 'active')
  const hasProductAccess = activeSubs.some(s => s.productId === Number(productId))

  // 개별 커리큘럼별 접근 권한 확인 함수
  const hasAccessToCurriculum = (contentId: number) => {
    return hasProductAccess || activeSubs.some(s => s.curriculumContentId === contentId)
  }

  // 구매 버튼 클릭
  const handlePurchase = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/products/${productId}` } })
      return
    }

    if (selectedPricingId) {
      navigate(`/checkout/${productId}?pricing=${selectedPricingId}`)
    }
  }

  // 특징 목록 파싱 (features가 string 배열 또는 JSON string일 수 있음)
  const parseFeatures = (): string[] => {
    if (!product) return []

    // features가 없으면 빈 배열
    const features = (product as unknown as { features?: string[] | string }).features
    if (!features) return []

    // 이미 배열이면 그대로 반환
    if (Array.isArray(features)) return features

    // string이면 줄바꿈으로 분리
    if (typeof features === 'string') {
      return features.split('\n').filter((f) => f.trim())
    }

    return []
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
        <p className="text-gray-600">상품 정보를 불러오는 중...</p>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <p className="text-gray-900 font-medium mb-2">상품을 찾을 수 없습니다</p>
        <Link to="/products" className="text-primary-600 hover:underline">
          상품 목록으로 돌아가기
        </Link>
      </div>
    )
  }

  const features = parseFeatures()

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Back Button & Breadcrumb */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/products" className="hover:text-gray-700">상품</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900">{product.name}</span>
        </nav>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left: Product Info */}
        <div className="lg:col-span-2 space-y-8">
          {/* Header */}
          <div>
            {product.status === 'active' && (
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  판매중
                </span>
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {product.name}
            </h1>
            {product.shortDescription && (
              <p className="text-lg text-gray-600">{product.shortDescription}</p>
            )}
          </div>

          {/* Thumbnail / Banner */}
          <div className="aspect-video bg-linear-to-br from-primary-100 to-primary-50 rounded-2xl overflow-hidden relative">
            {product.bannerUrl || product.thumbnailUrl ? (
              <img
                src={product.bannerUrl || product.thumbnailUrl || ''}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <BookOpen className="w-24 h-24 text-primary-300" />
              </div>
            )}

            {/* Preview Video Button (if available) */}
            {(product as unknown as { previewVideoUrl?: string }).previewVideoUrl && (
              <button className="absolute inset-0 flex items-center justify-center bg-black/30 group hover:bg-black/40 transition-colors">
                <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-primary-600 ml-1" />
                </div>
              </button>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">상품 설명</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{product.description}</p>
              </div>
            </div>
          )}

          {/* Connected Curriculums */}
          {product.contents && product.contents.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary-600" />
                연결된 커리큘럼
              </h2>
              <div className="space-y-4">
                {product.contents.map((content) => {
                  const hasAccess = hasAccessToCurriculum(content.contentId)
                  return (
                    <div
                      key={content.contentId}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-white border border-gray-200 rounded-2xl hover:border-primary-300 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center shrink-0">
                          <BookOpen className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                          <p className="text-xs text-primary-600 font-medium mb-1">{product.name}</p>
                          <h3 className="font-semibold text-gray-900">{content.title}</h3>
                          <p className="text-sm text-gray-500">순서: {content.sequenceOrder + 1}</p>
                        </div>
                      </div>
                      <Link
                        to={`/learn/curriculum/${content.contentId}`}
                        className={`btn px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${hasAccess
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200 cursor-not-allowed'
                          }`}
                        onClick={(e) => {
                          if (!hasAccess) {
                            e.preventDefault()
                            alert('이 커리큘럼을 학습하려면 먼저 상품을 구매해야 합니다.')
                          }
                        }}
                      >
                        <Play className="w-4 h-4" />
                        학습 시작하기
                      </Link>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Features */}
          {features.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                주요 특징
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Learning Outcomes */}
          {(product as unknown as { learningOutcomes?: string }).learningOutcomes && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">학습 성과</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {(product as unknown as { learningOutcomes?: string }).learningOutcomes}
              </p>
            </div>
          )}
        </div>

        {/* Right: Pricing & Purchase */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            {/* Pricing Card */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-lg">
              <div className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">가격 옵션</h2>

                {product.pricingOptions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    현재 이용 가능한 가격 옵션이 없습니다
                  </p>
                ) : (
                  <div className="space-y-3">
                    {product.pricingOptions.map((pricing) => (
                      <PricingOption
                        key={pricing.id}
                        pricing={pricing}
                        isSelected={selectedPricingId === pricing.id}
                        onSelect={() => setSelectedPricingId(pricing.id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Purchase Section */}
              {selectedPricing && (
                <div className="p-6 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">결제 금액</span>
                    <div className="text-right">
                      {selectedPricing.originalPrice && (
                        <span className="text-sm text-gray-400 line-through block">
                          {formatPrice(selectedPricing.originalPrice, selectedPricing.currency)}
                        </span>
                      )}
                      <span className="text-2xl font-bold text-primary-600">
                        {formatPrice(selectedPricing.price, selectedPricing.currency)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handlePurchase}
                    className="btn-primary w-full py-4 text-lg"
                  >
                    {isAuthenticated ? '구매하기' : '로그인 후 구매'}
                  </button>

                  {/* Trust Badges */}
                  <div className="mt-4 flex items-center justify-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      안전한 결제
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      즉시 이용
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Info */}
            <div className="bg-primary-50 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3">문의하기</h3>
              <p className="text-sm text-gray-600 mb-4">
                상품에 대해 궁금한 점이 있으시면 언제든 문의해주세요.
              </p>
              <a
                href="mailto:support@quebon.com"
                className="text-primary-600 text-sm font-medium hover:underline"
              >
                support@quebon.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sub Components
// ============================================================================

interface PricingOptionProps {
  pricing: PricingResponse
  isSelected: boolean
  onSelect: () => void
}

function PricingOption({ pricing, isSelected, onSelect }: PricingOptionProps) {
  const hasDiscount = pricing.originalPrice && pricing.discountPercentage

  return (
    <button
      onClick={onSelect}
      className={`w-full p-4 rounded-xl border-2 transition-all text-left relative ${isSelected
        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20'
        : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
    >
      {/* Default Badge */}
      {pricing.isDefault && (
        <span className="absolute -top-2 left-4 px-2 py-0.5 bg-primary-600 text-white text-xs font-medium rounded-full flex items-center gap-1">
          <Star className="w-3 h-3" />
          추천
        </span>
      )}

      {/* Selection Indicator */}
      <div
        className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected
          ? 'border-primary-500 bg-primary-500'
          : 'border-gray-300'
          }`}
      >
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Plan Name */}
      <h4 className="font-semibold text-gray-900 mb-1 pr-8">{pricing.name}</h4>

      {/* Billing Period */}
      <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
        <Calendar className="w-4 h-4" />
        <span>{getBillingPeriodLabel(pricing.billingPeriod)}</span>
        {pricing.maxSeats > 1 && (
          <span className="ml-2 flex items-center gap-1">
            <Users className="w-4 h-4" />
            {pricing.maxSeats}석
          </span>
        )}
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-bold text-gray-900">
          {formatPrice(pricing.price, pricing.currency)}
        </span>
        {hasDiscount && (
          <span className="text-sm text-gray-400 line-through">
            {formatPrice(pricing.originalPrice!, pricing.currency)}
          </span>
        )}
      </div>

      {/* Discount Badge */}
      {hasDiscount && (
        <span className="inline-block mt-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-sm">
          {pricing.discountPercentage}% 할인
        </span>
      )}

      {/* Trial Info */}
      {pricing.hasTrial && pricing.trialDays > 0 && (
        <p className="text-xs text-primary-600 mt-2">
          {pricing.trialDays}일 무료 체험 가능
        </p>
      )}
    </button>
  )
}





















